import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import { Button, Modal } from '../common';
import ProblemPreview from '../ProblemPreview';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const ProblemSelectModal = ({ 
  onClose, 
  selectedTree, 
  selectedAddress, 
  selectedStep, 
  pathInfo,
  fetchTreeData,
  fetchProblemCounts, 
  fetchWithTokenRefresh,
  handleProblemSelect 
}) => {
  const [modalProblems, setModalProblems] = useState([]);
  const [modalPage, setModalPage] = useState(1);
  const [modalHasMore, setModalHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [existingProblems, setExistingProblems] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    author: '',
    labels: ''
  });
  const [currentLocationProblems, setCurrentLocationProblems] = useState([]);
  const [recentlyModifiedIds, setRecentlyModifiedIds] = useState([]);
  const [initialExistingProblems, setInitialExistingProblems] = useState([]);
  const [previewInfo, setPreviewInfo] = useState({
    isOpen: false,
    problemId: [],
    problemNumber: null,
    isCorrect: null
  });
  const tableScrollRef = useRef(null);

  const fetchExistingProblems = useCallback(async () => {
    try {
      const response = await fetchWithTokenRefresh(
        `/api/referencefields/${selectedTree}/problems?address=${selectedAddress}&step=${selectedStep + 1}`
      );
      
      // 400 에러 처리 (OCR 미확인 문제)
      if (response?.status === 'error' && response?.status_code === 400) {
        if (response.message === 'Problem is not confirmed ocr') {
          console.error('OCR이 확인되지 않은 문제가 있습니다.');
          toast.error('OCR이 확인되지 않은 문제가 있습니다.');
          // 기존 문제 목록 초기화
          setExistingProblems([]);
          setInitialExistingProblems([]);
        } else {
          console.error(response.message || '문제 정보를 불러올 수 없습니다.');
        }
      } else if (response && response.status === 'success') {
        setExistingProblems(response.data.problems);
        setInitialExistingProblems(response.data.problems); // Store initial state
      }
    } catch (error) {
      console.error('기존 문제 목록 불러오기 실패:', error);
      if (error.status === 409) {
        toast.error('닫히지 않은 트리입니다.');
      }
    }
  }, [fetchWithTokenRefresh, selectedTree, selectedAddress, selectedStep]);

  const fetchModalProblems = useCallback(async (resetPage = false, useFilter = false) => {
    if (isLoading) return;
    
    // useFilter 매개변수만 사용하고 isFilterMode 상태를 직접 참조하지 않음
    const shouldUseFilter = useFilter;
    const currentPage = resetPage ? 1 : modalPage;
    
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: 20
      });

      if (shouldUseFilter) {
        if (filters.status) params.append('status', filters.status);
        if (filters.author) params.append('author', filters.author);
        if (filters.labels) params.append('labels', filters.labels);
      }

      const hasFilterValues = filters.status || filters.author || filters.labels;
      const endpoint = shouldUseFilter && hasFilterValues ? 
        '/api/problems/filtered' : 
        '/api/problems/';

      const response = await fetchWithTokenRefresh(`${endpoint}?${params}`);
      
      if (!response) return;

      if (response.status === 'success') {
        if (resetPage) {
          setModalProblems(response.data);
          setModalPage(2);
        } else {
          const newProblems = response.data || [];
          setModalProblems(prev => {
            const existingIds = new Set(prev.map(p => p.problem_id));
            const uniqueNewProblems = newProblems.filter(p => !existingIds.has(p.problem_id));
            return [...prev, ...uniqueNewProblems];
          });
          setModalPage(prev => prev + 1);
        }
        setModalHasMore(response.data && response.data.length === 20);
      }
    } catch (error) {
      console.error('문제 목록 불러오기 실패:', error);
      if (error.status === 404) {
        setModalHasMore(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [modalPage, filters, isLoading, fetchWithTokenRefresh]);

  const fetchCurrentLocationProblems = useCallback(async () => {
    try {
      // Use initialExistingProblems to keep the list stable
      const currentProblems = initialExistingProblems.filter(
        ep => ep.address === selectedAddress && ep.step === selectedStep + 1
      );

      if (currentProblems.length === 0) {
        setCurrentLocationProblems([]);
        return;
      }

      const problemIds = currentProblems.map(p => p.problem_id).join(',');

      const response = await fetchWithTokenRefresh(
        `/api/problems/filtered?problem_ids=${problemIds}`
      );

      if (response.status === 'success') {
        setCurrentLocationProblems(response.data);
      }
    } catch (error) {
      console.error('현재 위치 문제 정보 조회 실패:', error);
    }
  }, [initialExistingProblems, selectedAddress, selectedStep, fetchWithTokenRefresh]);

  const handleReset = useCallback(() => {
    setFilters({
      status: '',
      author: '',
      labels: ''
    });
    setModalProblems([]);
    setModalPage(1);
    setModalHasMore(true);
    // 직접 false를 전달하여 필터 없이 조회
    fetchModalProblems(true, false);
  }, [fetchModalProblems]);

  // Initial load - run only once when component mounts
  useEffect(() => {
    // Load existing problems first, then modal problems
    const loadInitialData = async () => {
      await fetchExistingProblems();
      await fetchModalProblems(true, false);
      // Don't call fetchCurrentLocationProblems here - it will be triggered by existingProblems change
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array for initial load only

  // Fetch current location problems when initialExistingProblems changes
  useEffect(() => {
    if (initialExistingProblems.length > 0) {
      fetchCurrentLocationProblems();
      // Reset scroll position to top when initialExistingProblems loads
      if (tableScrollRef.current) {
        tableScrollRef.current.scrollTop = 0;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, [initialExistingProblems]); // Trigger when initialExistingProblems updates

  // Global ESC key handler for search reset (only when not in input fields)
  useEffect(() => {
    const handleGlobalEsc = (e) => {
      // 입력 필드가 포커스되어 있으면 전역 핸들러는 실행하지 않음
      const target = e.target;
      const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
      
      if (e.key === 'Escape' && !isInputField) {
        e.preventDefault();
        handleReset();
      }
    };

    document.addEventListener('keydown', handleGlobalEsc);
    return () => document.removeEventListener('keydown', handleGlobalEsc);
  }, [handleReset]);

  // Handle filter mode changes - removed to prevent conflicts with handleReset

  const handleScroll = useCallback((e) => {
    const element = e.target;
    if (!modalHasMore || isLoading) return;

    const scrollBottom = Math.max(0, element.scrollHeight - element.scrollTop - element.clientHeight);
    // Trigger when scrolled to within 100px of the bottom
    if (scrollBottom <= 100) {
      // isFilterMode 대신 현재 필터 값이 있는지 확인
      const hasFilterValues = filters.status || filters.author || filters.labels;
      fetchModalProblems(false, hasFilterValues);
    }
  }, [modalHasMore, isLoading, filters, fetchModalProblems]);

  const handleProblemDelete = async (problemId) => {
    if (!window.confirm('이 문제를 삭제하시겠습니까?')) return;

    try {
      const response = await fetchWithTokenRefresh(`/api/referencefields/${selectedTree}/problems`, {
        method: 'DELETE',
        body: JSON.stringify({
          problem_id: problemId,
          address: `${selectedAddress}`,
          step: selectedStep + 1
        })
      });

      if (response && response.status === 'success') {
        setRecentlyModifiedIds(prev => [...prev, problemId]);
        toast.success('문제가 성공적으로 삭제되었습니다.');
        
        // existingProblems에서 해당 문제 제거
        setExistingProblems(prev => 
          prev.filter(ep => 
            !(ep.problem_id === problemId && 
              ep.address === `${selectedAddress}` && 
              ep.step === selectedStep + 1)
          )
        );
        
        // 문제 수 업데이트
        if (fetchProblemCounts) {
          await fetchProblemCounts();
        }
      }
    } catch (error) {
      console.error('문제 삭제 실패:', error);
      toast.error('문제 삭제에 실패했습니다.');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    const hasFilterValues = filters.status || filters.author || filters.labels;
    if (!hasFilterValues) {
      toast.error('상태, 작성자, 라벨 중 하나 이상을 입력해주세요.');
      return;
    }
    fetchModalProblems(true, true);
  };

  const handlePreview = async (problemId) => {
    setPreviewInfo({
      isOpen: true,
      problemId: [problemId],
      problemNumber: null,
      isCorrect: null
    });
  };

  const copyToClipboard = async (text) => {
    const formattedText = text.replace(/\//g, '_');
    
    try {
      // Modern clipboard API (may not work on all mobile browsers)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(formattedText);
        toast.success('클립보드에 복사되었습니다.');
      } else {
        // Fallback method for mobile and older browsers
        const textArea = document.createElement("textarea");
        textArea.value = formattedText;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          toast.success('클립보드에 복사되었습니다.');
        } catch (err) {
          toast.error('복사에 실패했습니다.');
        } finally {
          textArea.remove();
        }
      }
    } catch (err) {
      toast.error('복사에 실패했습니다.');
    }
  };

  const refreshModalContent = useCallback(async () => {
    // 현재 필터 값이 있는지 확인
    const hasFilterValues = filters.status || filters.author || filters.labels;
    await Promise.all([
      fetchModalProblems(true, hasFilterValues),
      fetchCurrentLocationProblems(),
      // 문제 수 실시간 업데이트
      fetchProblemCounts ? fetchProblemCounts() : Promise.resolve()
    ]);
    setModalPage(2);
    setModalHasMore(true);
  }, [filters, fetchModalProblems, fetchCurrentLocationProblems, fetchProblemCounts]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleReset();
    }
  };

  const handleClose = async () => {
    setRecentlyModifiedIds([]);
    // 전체 트리 데이터 대신 문제 수만 업데이트
    if (fetchProblemCounts) {
      await fetchProblemCounts();
    }
    onClose();
  };


  return (
    <>
      {createPortal(
        <Toaster 
          position="top-center" 
          containerStyle={{
            position: 'fixed',
            zIndex: 10000000
          }}
          toastOptions={{ 
            style: { 
              zIndex: 10000000 
            } 
          }} 
        />,
        document.body
      )}
      <Modal
        isOpen={true}
        onClose={handleClose}
        title="문제 선택"
        size="xl"
        showCloseButton={true}
        closeOnBackdropClick={true}
        closeOnEsc={false}
        bodyClassName="flex flex-col p-0 min-h-0"
        zIndex={9999920}
      >
        <div className="flex flex-col h-full min-h-0 overflow-hidden">

        <div className="flex-shrink-0 mb-3 p-4 bg-gray-50 rounded-3xl space-x-2 text-sm">
          <span>
            <span className="cursor-pointer hover:text-primary transition-colors" 
                  onClick={() => copyToClipboard(pathInfo.depth1.name)}>
              {pathInfo.depth1.name}
            </span>
            <span> (</span>
            <span className="cursor-pointer hover:text-primary transition-colors" 
                  onClick={() => copyToClipboard(pathInfo.depth1.path_id)}>
              {pathInfo.depth1.path_id}
            </span>
            <span>)</span>
          </span>
          <span>|</span>
          <span>
            <span className="cursor-pointer hover:text-primary transition-colors" 
                  onClick={() => copyToClipboard(pathInfo.depth2.name)}>
              {pathInfo.depth2.name}
            </span>
            <span> (</span>
            <span className="cursor-pointer hover:text-primary transition-colors" 
                  onClick={() => copyToClipboard(pathInfo.depth2.path_id)}>
              {pathInfo.depth2.path_id}
            </span>
            <span>)</span>
          </span>
          <span>|</span>
          <span>
            <span className="cursor-pointer hover:text-primary transition-colors" 
                  onClick={() => copyToClipboard(pathInfo.depth3.name)}>
              {pathInfo.depth3.name}
            </span>
            <span> (</span>
            <span className="cursor-pointer hover:text-primary transition-colors" 
                  onClick={() => copyToClipboard(pathInfo.depth3.path_id)}>
              {pathInfo.depth3.path_id}
            </span>
            <span>)</span>
          </span>
          <span>|</span>
          <span>
            <span className="cursor-pointer hover:text-primary transition-colors" 
                  onClick={() => copyToClipboard(pathInfo.depth4.name)}>
              {pathInfo.depth4.name}
            </span>
            <span> (</span>
            <span className="cursor-pointer hover:text-primary transition-colors" 
                  onClick={() => copyToClipboard(pathInfo.depth4.path_id)}>
              {pathInfo.depth4.path_id}
            </span>
            <span>)</span>
          </span>
          <span>|</span>
          <span className="cursor-pointer hover:text-primary transition-colors" 
                onClick={() => copyToClipboard(`${selectedStep + 1}`)}>
            난이도 {selectedStep + 1}
          </span>
        </div>

        <div className="flex-shrink-0 flex gap-3 mb-3">
          <div className="flex flex-col sm:flex-row flex-1 gap-3">
            <input
              type="text"
              name="author"
              placeholder="작성자"
              value={filters.author}
              onChange={handleFilterChange}
              onKeyDown={handleKeyDown}
              className="flex-1 min-w-0 rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <input
              type="text"
              name="labels"
              placeholder="라벨"
              value={filters.labels}
              onChange={handleFilterChange}
              onKeyDown={handleKeyDown}
              className="flex-1 min-w-0 rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:flex-shrink-0">
            <Button
              onClick={handleReset}
              variant="secondary"
              size="sm"
              className="h-full sm:h-auto sm:order-2"
            >
              초기화
            </Button>
            <Button
              onClick={handleSearch}
              variant="primary"
              size="sm"
              className="h-full sm:h-auto sm:order-1"
            >
              조회
            </Button>
          </div>
        </div>

        <div className="flex-1 min-h-0" style={{ 
          display: 'grid',
          gridTemplateRows: currentLocationProblems.length > 0 ? '40% 60%' : '1fr',
          gap: '12px',
          height: '100%'
        }}>
        {currentLocationProblems.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            <h3 className="mb-3 text-lg font-semibold flex-shrink-0">현재 위치의 문제 목록</h3>
            <div className="overflow-x-auto overflow-y-auto rounded-xl border border-gray-200 flex-1" style={{ minWidth: 0 }}>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">ID</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">작성자</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">라벨</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">미리보기</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">작업</th>
                  </tr>
                </thead>
              <tbody>
                {currentLocationProblems.map((problem) => {
                  const isDeleted = !existingProblems.some(
                    ep => ep.problem_id === problem.problem_id && 
                         ep.address === `${selectedAddress}` &&
                         ep.step === selectedStep + 1
                  );
                  
                  return (
                    <tr key={problem.problem_id} 
                        className={`border-b ${recentlyModifiedIds.includes(problem.problem_id) ? 'bg-primary/20' : 'bg-warning/10'}`}>
                      <td className="px-2 py-2 whitespace-nowrap">{problem.problem_id}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{problem.author}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{problem.labels}</td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <button
                          onClick={() => handlePreview(problem.problem_id)}
                          className="text-primary hover:text-primary-hover transition-colors font-medium"
                        >
                          미리보기
                        </button>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        {isDeleted ? (
                          <button
                            onClick={async () => {
                              if (!window.confirm('이 문제를 선택하시겠습니까?')) return;
                              const success = await handleProblemSelect(problem.problem_id);
                              
                              if (success) {
                                setRecentlyModifiedIds(prev => [...prev, problem.problem_id]);
                                setExistingProblems(prev => [...prev, {
                                  problem_id: problem.problem_id,
                                  address: `${selectedAddress}`,
                                  step: selectedStep + 1
                                }]);
                              }
                            }}
                            className="text-primary hover:text-primary-hover transition-colors font-medium"
                          >
                            선택
                          </button>
                        ) : (
                          <button
                            onClick={() => handleProblemDelete(problem.problem_id)}
                            className="text-danger hover:text-danger-hover transition-colors font-medium"
                          >
                            삭제
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div ref={tableScrollRef} 
             className="overflow-x-auto overflow-y-auto rounded-xl border border-gray-200" 
             style={{ minHeight: 0, minWidth: 0, overflow: 'auto' }}
             onScroll={handleScroll}>
          {isLoading && modalProblems.length === 0 && (
            <div className="py-8 text-center text-gray-500">로딩 중...</div>
          )}
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">ID</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">작성자</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">라벨</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">미리보기</th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">작업</th>
              </tr>
            </thead>
            <tbody>
            {modalProblems.map((problem) => {
              const existingProblemAtCurrentLocation = existingProblems.some(
                ep => ep.problem_id === problem.problem_id && 
                     ep.address === `${selectedAddress}` &&
                     ep.step === selectedStep + 1
              );

              const existingProblemAtOtherLocation = existingProblems.some(
                ep => ep.problem_id === problem.problem_id && 
                     (ep.address !== `${selectedAddress}` || ep.step !== selectedStep + 1)
              );

              return (
                <tr key={problem.problem_id} 
                    className={`border-b transition-colors ${
                      recentlyModifiedIds.includes(problem.problem_id) ? 'bg-primary/20' :
                      existingProblemAtCurrentLocation ? 'bg-green-100' : 
                      existingProblemAtOtherLocation ? 'bg-danger/5' : 
                      'hover:bg-gray-50'
                    }`}>
                  <td className="px-2 py-2 whitespace-nowrap">{problem.problem_id}</td>
                  <td className="px-2 py-2 whitespace-nowrap">{problem.author}</td>
                  <td className="px-2 py-2 whitespace-nowrap">{problem.labels}</td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <button
                      onClick={() => handlePreview(problem.problem_id)}
                      className="text-primary hover:text-primary-hover transition-colors font-medium"
                    >
                      미리보기
                    </button>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {existingProblemAtCurrentLocation ? (
                      <button
                        onClick={async () => {
                          await handleProblemDelete(problem.problem_id);
                        }}
                        className="text-danger hover:text-danger-hover transition-colors font-medium"
                      >
                        삭제
                      </button>
                    ) : !existingProblemAtOtherLocation && (
                      <button
                        onClick={async () => {
                          if (!window.confirm('이 문제를 선택하시겠습니까?')) return;
                          const success = await handleProblemSelect(problem.problem_id);
                          
                          // 성공한 경우에만 상태 업데이트
                          if (success) {
                            setRecentlyModifiedIds(prev => [...prev, problem.problem_id]);
                            
                            // existingProblems에 새 문제 추가
                            setExistingProblems(prev => [...prev, {
                              problem_id: problem.problem_id,
                              address: `${selectedAddress}`,
                              step: selectedStep + 1
                            }]);
                            
                            // currentLocationProblems에 문제 정보 추가
                            setCurrentLocationProblems(prev => {
                              // 이미 존재하는지 확인
                              if (prev.some(p => p.problem_id === problem.problem_id)) {
                                return prev;
                              }
                              return [...prev, problem];
                            });
                          }
                        }}
                        className="text-primary hover:text-primary-hover transition-colors font-medium"
                      >
                        선택
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!isLoading && modalProblems.length === 0 && (
              <tr>
                <td colSpan="5" className="py-8 text-center text-gray-500">데이터가 없습니다.</td>
              </tr>
            )}
            {isLoading && modalProblems.length > 0 && (
              <tr>
                <td colSpan="5" className="py-4 text-center text-gray-500 text-sm">로딩 중...</td>
              </tr>
            )}
            {!modalHasMore && modalProblems.length > 0 && !isLoading && (
              <tr>
                <td colSpan="5" className="py-3 text-center text-gray-400 text-sm">더 이상 불러올 문제가 없습니다.</td>
              </tr>
            )}
            </tbody>
          </table>
        </div>
        </div>
        
        </div>
      </Modal>
      <ProblemPreview
        isOpen={previewInfo.isOpen}
        onClose={() => setPreviewInfo({
          isOpen: false,
          problemId: [],
          problemNumber: null,
          isCorrect: null
        })}
        problemIds={previewInfo.problemId}
        showNumbers={false}
        correctList={[]}
        problemNumbers={[]}
      />
    </>
  );
};

export default ProblemSelectModal;
